/* eslint-disable react/jsx-no-duplicate-props */
import { forwardRef, useCallback, useEffect, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Search from '@material-ui/icons/Search';
import { alpha, CircularProgress, Fade, IconButton, TextField, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import { t } from '@lingui/macro';
import { isEmpty } from 'lodash';

import { useIsPgm } from '../../stores/EnvironmentStore';
import useDebounced from '../../utilities/UseDebounced';
import { Actions, useDispatcher, useStore } from './UseGlobalSearchStore';
import { useUserGroups } from '../../stores/SessionStore';

const useStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentCenter,

        transistionProperty: 'background, width',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease',

        width: '24px',
        height: '100%',

        '@media print': {
            display: 'none',
        },

        '&.expanded': {
            padding: theme.spacing(0, 2),
            width: (props) => props.width,

            '&.inactive': {
                backgroundColor: alpha(theme.palette.background.paper, 0.15),

                '& $searchButton': {
                    color: theme.palette.primary.contrastText,
                },
            },

            '&.active': {
                backgroundColor: alpha(theme.palette.background.paper, 0.8),

                '& $searchButton': {
                    color:
                        theme.palette.type === 'light'
                            ? theme.palette.text.primary
                            : theme.palette.primary.contrastText,
                },
            },
        },
    },

    spacer: {
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.shrink(1),
    },

    loadingIndicator: {
        margin: theme.spacing(0, 1),
    },

    searchButton: {
        width: 24,
        padding: 0,
    },
}));

function GlobalSearchView(props, ref) {
    const { isExpanded, isActive, toggleIsExpanded, activate } = props;
    const classes = useStyles(props);

    const [searchValue, setSearchValue] = useState('');
    const [search, setSearch] = useState(searchValue);
    const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
    const debouncedSetSearch = useDebounced(setSearch, 250);
    const debouncedSetShowLoadingIndicator = useDebounced(setShowLoadingIndicator, 400);
    const dispatcher = useDispatcher();
    const { isLoading } = useStore();
    const [isPgm] = useIsPgm();
    const [userGroups] = useUserGroups();

    const onFocus = useCallback(() => {
        if (!isEmpty(search)) {
            Actions.search(dispatcher, search, isPgm, userGroups);
        }

        activate();
    }, [activate, search, dispatcher, isPgm, userGroups]);

    // set the search value, but in a debounced manner
    useEffect(() => {
        debouncedSetSearch(searchValue);
    }, [debouncedSetSearch, searchValue]);

    // only change the loading indicator if the call takes 400+ ms, otherwise we flicker
    useEffect(() => {
        if (isLoading) {
            debouncedSetShowLoadingIndicator(true);
        } else {
            debouncedSetShowLoadingIndicator.cancel();
            setShowLoadingIndicator(false);
        }
    }, [isLoading, debouncedSetShowLoadingIndicator, setShowLoadingIndicator]);

    // Reset results when the search changes. We wouldn't want to merge anything in
    useEffect(() => {
        if (!search) {
            Actions.reset(dispatcher);
        } else {
            Actions.search(dispatcher, search, isPgm, userGroups);
        }
    }, [search, dispatcher, isPgm, userGroups]);

    const modeCls = {
        expanded: isExpanded,
        inactive: !isActive,
        active: isActive,
    };

    return (
        <div className={clsx(classes.root, modeCls)} ref={ref}>
            {isExpanded ? (
                <GlobalSearchTextField value={searchValue} onChange={setSearchValue} onFocus={onFocus} />
            ) : (
                <div className={classes.spacer} />
            )}

            <Tooltip title={t`Show or hide the search bar`} placement="bottom">
                <IconButton className={classes.searchButton} onClick={toggleIsExpanded}>
                    {showLoadingIndicator && isActive ? (
                        <Fade in={showLoadingIndicator} timeout={200}>
                            <CircularProgress size={20} />
                        </Fade>
                    ) : (
                        <Search />
                    )}
                </IconButton>
            </Tooltip>
        </div>
    );
}

export default forwardRef(GlobalSearchView);

const useTextStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentCenter,

        padding: 0,
        margin: 0,
        fontSize: '18px',
        lineHeight: '18px',
    },

    inputEl: {
        '&, & input': {
            padding: 0,
            margin: 0,
        },
    },
}));

function GlobalSearchTextField(props) {
    const { value, onChange, onFocus } = props;
    const classes = useTextStyles();

    return (
        <TextField
            fullWidth
            autoFocus
            value={value}
            className={classes.root}
            name="GlobalSearch"
            placeholder={t`Search...`}
            size="small"
            onFocus={onFocus}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{
                maxLength: 100,
            }}
            InputProps={{
                className: classes.inputEl,
                disableUnderline: true,
            }}
        />
    );
}
